import { Countries } from '../constants/Countries';
import {
  GOOGLE_REVIEW_PROFILES,
  GOOGLE_REVIEW_URL_SG,
  GOOGLE_REVIEW_URL_UAE,
  TGoogleReviewProfile,
  UAE_COUNTRY_CODE,
  UAE_DIAL_CODE,
  UAE_TIMEZONES,
} from '../constants/googleReview';
import { getCountry } from './Strings';

/**
 * Signals used to decide which Tutopiya Google Business Profile a student is
 * sent to. All of them are best-effort — when none says UAE we fall back to the
 * Singapore profile, which is the correct default for the rest of the world.
 */
export interface IGoogleReviewGeoInput {
  /** `IUser.country` — an IANA timezone (e.g. `Asia/Dubai`), not an ISO code. */
  profileTimezone?: string;
  /** `IUser.phoneNumber` — any format; only the digits are inspected. */
  phoneNumber?: string;
}

const isUaeTimezone = (timezone?: string): boolean => {
  if (!timezone) {
    return false;
  }

  if (UAE_TIMEZONES.includes(timezone)) {
    return true;
  }

  const row = Countries.find((item) => item.value === timezone);

  return row?.countryCode?.toUpperCase() === UAE_COUNTRY_CODE;
};

/**
 * `+971 50 123 4567`, `00971501234567` and `971501234567` all resolve to UAE.
 * The length guard keeps a local number that merely starts with 971 from
 * matching (UAE numbers are 971 + 9 national digits).
 */
const isUaePhoneNumber = (phoneNumber?: string): boolean => {
  if (!phoneNumber) {
    return false;
  }

  const digits = phoneNumber.replace(/\D/g, '');
  const international = digits.startsWith('00') ? digits.slice(2) : digits;

  return international.startsWith(UAE_DIAL_CODE) && international.length >= 11;
};

/**
 * UAE if any signal says so: the timezone on the student's profile, the browser
 * timezone, or a UAE phone number. Anything else is treated as rest-of-world.
 */
export const isUaeStudent = ({
  profileTimezone,
  phoneNumber,
}: IGoogleReviewGeoInput = {}): boolean =>
  isUaeTimezone(profileTimezone) ||
  isUaeTimezone(getCountry()) ||
  isUaePhoneNumber(phoneNumber);

export const getGoogleReviewProfile = (
  input: IGoogleReviewGeoInput = {}
): TGoogleReviewProfile =>
  isUaeStudent(input) ? GOOGLE_REVIEW_PROFILES.UAE : GOOGLE_REVIEW_PROFILES.SG;

export const getGoogleReviewUrl = (input: IGoogleReviewGeoInput = {}): string =>
  isUaeStudent(input) ? GOOGLE_REVIEW_URL_UAE : GOOGLE_REVIEW_URL_SG;

/**
 * Copies the student's feedback so they only have to paste it on Google.
 * Falls back to the legacy `execCommand` path for browsers that block the async
 * clipboard API outside a direct user gesture.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (!text) {
    return false;
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API unavailable or blocked — try the legacy path below.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

/**
 * Opens the Google review page in a new tab. Returns false when the browser
 * blocked the popup, so the caller can fall back to an explicit button.
 */
export const openGoogleReviewPage = (url: string): boolean => {
  // Passing 'noopener' makes window.open always return null, so it could not
  // tell a blocked popup apart from an opened one. Detach the opener manually.
  const openedWindow = window.open(url, '_blank');

  if (openedWindow) {
    openedWindow.opener = null;
  }

  return !!openedWindow;
};
